/**
 * After queueing Celery per-robot GS gap fills, poll a **light** sync-status endpoint until `ready`,
 * then fetch the **heavy** cache_only snapshot once (or on timeout, best-effort).
 *
 * If POST actually queued Celery tasks (`queued > 0`, not `deduped`), we poll longer (see
 * `MAX_TICKS_WHEN_CELERY_QUEUED_MIN`) because partial DB writes can finish at different times.
 */

export const GAUSIUM_GAP_DEBOUNCE_MS = 350
/** Wait after POST before the first sync-status poll. */
export const GAUSIUM_GAP_FIRST_POLL_MS = 2000
/** Shorter interval between polls for the first few ticks. */
export const GAUSIUM_GAP_FAST_POLL_MS = 1800
/** After POST, use fast interval after each of the first N successful poll ticks, then `GAUSIUM_GAP_POLL_MS`. */
export const GAUSIUM_GAP_FAST_POLL_TICKS = 6
export const GAUSIUM_GAP_POLL_MS = 8000
export const GAUSIUM_GAP_MAX_TICKS = 18
/** When Celery work was queued, poll at least this many ticks (slow phase dominates). */
const MAX_TICKS_WHEN_CELERY_QUEUED_MIN = 72

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

export type GausiumGapQueueResult = { queued: number; robot_ids?: number[]; deduped?: boolean }

export type GausiumSyncStatus = { state: 'syncing' | 'ready'; pending?: number }
export type GausiumDeltaResponse = {
  results: Array<{ event_id?: string; updated_at?: string }>
  next_cursor: string
  count?: number
}

export function buildDeltaCursorFromRows(rows: Array<{ event_id?: string; updated_at?: string }>): string {
  let bestUpdated = ''
  let bestEventId = ''
  for (const row of rows) {
    const u = String(row.updated_at ?? '')
    const e = String(row.event_id ?? '')
    if (!u || !e) continue
    if (u > bestUpdated || (u === bestUpdated && e > bestEventId)) {
      bestUpdated = u
      bestEventId = e
    }
  }
  return bestUpdated && bestEventId ? `${bestUpdated}|${bestEventId}` : ''
}

/**
 * 1) debounce 2) POST queue 3) poll light status + incremental GS deltas until `ready`.
 */
export async function runGausiumGapRefreshFollowup(options: {
  debounceMs: number
  firstPollDelayMs: number
  pollIntervalMs: number
  maxTicks: number
  /** Kept for API compatibility; not used (status endpoint replaces fingerprint stability). */
  minTicksBeforeStable: number
  stableRounds: number
  queueRefresh: () => Promise<GausiumGapQueueResult | void>
  /** Light GET: `{ state, pending }` — always small JSON. */
  fetchSyncStatus: () => Promise<GausiumSyncStatus>
  /** Cursor from initial cache_only snapshot, e.g. `${updated_at}|${event_id}` (optional). */
  initialCursor?: string
  /** Lightweight incremental payload of rows updated strictly after `cursor`. */
  fetchDelta: (cursor: string) => Promise<GausiumDeltaResponse>
  isStale: () => boolean
  onDelta: (rows: Array<{ event_id?: string; updated_at?: string }>) => void
  fastPollIntervalMs?: number
  fastPollTicks?: number
}): Promise<void> {
  await sleep(options.debounceMs)
  if (options.isStale()) return

  let queueResult: GausiumGapQueueResult | void
  try {
    queueResult = await options.queueRefresh()
  } catch (err) {
    console.warn('[Gausium gap] queueRefresh failed', err)
    return
  }
  if (
    queueResult &&
    typeof queueResult.queued === 'number' &&
    queueResult.queued === 0 &&
    !queueResult.deduped
  ) {
    console.warn(
      '[Gausium gap] queued=0: no Gausium robots for this user, or POST was skipped; check Network POST gausium-normalized-gap-refresh'
    )
  }
  if (options.isStale()) return

  const queuedCeleryWork = Boolean(
    queueResult &&
      typeof queueResult.queued === 'number' &&
      queueResult.queued > 0 &&
      !queueResult.deduped
  )

  await sleep(options.firstPollDelayMs)
  if (options.isStale()) return

  const maxTicks = queuedCeleryWork
    ? Math.max(options.maxTicks, MAX_TICKS_WHEN_CELERY_QUEUED_MIN)
    : options.maxTicks

  const fastMs = options.fastPollIntervalMs ?? GAUSIUM_GAP_FAST_POLL_MS
  const fastTickLimit = options.fastPollTicks ?? GAUSIUM_GAP_FAST_POLL_TICKS
  const delayAfterPollTick = (pollTick: number) =>
    pollTick <= fastTickLimit ? fastMs : options.pollIntervalMs

  let tick = 0
  let cursor = options.initialCursor ?? ''
  while (tick < maxTicks) {
    if (options.isStale()) return
    tick += 1

    let status: GausiumSyncStatus
    try {
      status = await options.fetchSyncStatus()
    } catch (e) {
      console.warn('[Gausium gap] fetchSyncStatus failed', e)
      await sleep(delayAfterPollTick(tick))
      continue
    }
    if (options.isStale()) return

    try {
      const delta = await options.fetchDelta(cursor)
      if (!options.isStale()) {
        const rows = delta.results ?? []
        if (rows.length > 0) {
          options.onDelta(rows)
        }
        if (delta.next_cursor) {
          cursor = delta.next_cursor
        } else if (rows.length > 0) {
          const fallbackCursor = buildDeltaCursorFromRows(rows)
          if (fallbackCursor) cursor = fallbackCursor
        }
      }
    } catch (e) {
      console.warn('[Gausium gap] fetchDelta failed', e)
    }
    if (options.isStale()) return

    if (status.state === 'ready') {
      return
    }

    await sleep(delayAfterPollTick(tick))
    if (options.isStale()) return
  }

  // Timeout: make one last delta attempt.
  try {
    const delta = await options.fetchDelta(cursor)
    if (!options.isStale()) {
      const rows = delta.results ?? []
      if (rows.length > 0) {
        options.onDelta(rows)
      }
    }
  } catch (e) {
    console.warn('[Gausium gap] fetchDelta (timeout) failed', e)
  }
}
